import { Link } from "react-router-dom";
import { ArrowRight, Ear, Eye, Mic, Repeat, Sparkles, Volume2 } from "lucide-react";
import lexiaPortrait768Webp from "@/assets/lexia/portrait-768.webp";
import lexiaPortrait768Jpg from "@/assets/lexia/portrait-768.jpg";
import lexiaPortrait1080Webp from "@/assets/lexia/portrait-1080.webp";
import lexiaPortrait1080Jpg from "@/assets/lexia/portrait-1080.jpg";
import SiteFooter from "@/components/SiteFooter";

const STEPS = [
    { icon: Sparkles, label: "Mot", detail: "Un lexème pertinent, choisi pour vous." },
    { icon: Eye, label: "Sens", detail: "Traduction et registre, sans faux-ami." },
    { icon: Volume2, label: "Prononciation", detail: "IPA + approximation en français." },
    { icon: Sparkles, label: "Image mentale", detail: "Une scène mnémotechnique qui colle." },
    { icon: Eye, label: "Phrase", detail: "Un exemple naturel, pas scolaire." },
    { icon: Ear, label: "Contexte", detail: "Situation réelle où le mot vit." },
    { icon: Ear, label: "Écoute", detail: "Version naturelle et ralentie." },
    { icon: Mic, label: "Production", detail: "Vous le dites, l'IA vous coache." },
    { icon: Repeat, label: "Révision", detail: "Retour au bon moment, pas de cramming." },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="landing-page">
            <header className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                    <span className="text-2xl font-serif tracking-tight">
                        Lexia<span className="text-terracotta"> </span>School
                    </span>
                </Link>
                <nav className="flex items-center gap-4 text-sm">
                    <Link
                        to="/login"
                        className="text-ink hover:text-terracotta transition-colors"
                        data-testid="nav-login"
                    >
                        Connexion
                    </Link>
                    <Link
                        to="/register"
                        className="inline-flex items-center gap-1.5 bg-ink hover:bg-emerald text-white rounded-full px-4 py-2 text-sm transition-colors"
                        data-testid="nav-register"
                    >
                        Commencer
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </nav>
            </header>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="max-w-3xl">
                    <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-6" data-testid="hero-eyebrow">
                        Un tuteur linguistique qui écoute autant qu&apos;il enseigne
                    </p>
                    <h1
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                        data-testid="hero-title"
                    >
                        Un mot{" "}
                        <span className="italic text-terracotta">entendu, vu, prononcé</span>{" "}
                        et utilisé devient un mot mémorisé.
                    </h1>
                    <p
                        className="mt-6 text-base sm:text-lg text-inkSoft leading-relaxed max-w-2xl"
                        data-testid="hero-blurb"
                    >
                        Lexia School conçoit votre parcours mot par mot : lexique choisi pour vous,
                        prononciation coachée, révisions calibrées CECRL, conversations IA. Le
                        tout en français, sur mesure, sans slop.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link
                            to="/register"
                            data-testid="hero-cta-register"
                            className="group inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white px-6 py-3 rounded-full text-sm font-medium transition-colors active:scale-[0.98]"
                        >
                            Créer un compte gratuit
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                        <Link
                            to="/login"
                            data-testid="hero-cta-login"
                            className="inline-flex items-center gap-2 bg-white hover:bg-paperAlt border border-lexiBorder text-ink px-6 py-3 rounded-full text-sm font-medium transition-colors"
                        >
                            J&apos;ai déjà un compte
                        </Link>
                    </div>
                </div>

                {/* Meet Lexia — coach persona */}
                <div
                    className="mt-24 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-10 lg:gap-14 items-center"
                    data-testid="landing-meet-lexia"
                >
                    <div>
                        <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">
                            Votre coach vocale
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
                            Voici Lexia,<br />
                            <span className="text-terracotta">la voix qui vous accompagne.</span>
                        </h2>
                        <p className="mt-5 text-inkSoft leading-relaxed max-w-lg">
                            Chaleureuse, patiente, exigeante quand il le faut. Lexia écoute votre prononciation,
                            corrige avec bienveillance, revient sur ce qui coince — et vous fait parler{" "}
                            <em>vraiment</em>, dès la première minute.
                        </p>
                        <ul className="mt-6 space-y-2.5 text-sm text-ink">
                            <li className="inline-flex items-center gap-2">
                                <Mic className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                                Sessions vocales temps réel — comme parler à une vraie prof.
                            </li>
                            <li className="inline-flex items-center gap-2">
                                <Ear className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                                Elle vous entend, s&apos;adapte, et vous répète autrement si besoin.
                            </li>
                            <li className="inline-flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                                Question du jour, révisions guidées, mini-conversations libres.
                            </li>
                        </ul>
                    </div>
                    <figure className="relative mx-auto lg:mx-0 w-full max-w-[380px]" data-testid="landing-lexia-portrait">
                        <div className="absolute -inset-3 rounded-3xl bg-terracotta/12 -rotate-2" aria-hidden="true" />
                        <picture>
                            <source
                                type="image/webp"
                                srcSet={`${lexiaPortrait768Webp} 768w, ${lexiaPortrait1080Webp} 1080w`}
                                sizes="(min-width: 1024px) 380px, 100vw"
                            />
                            <img
                                src={lexiaPortrait768Jpg}
                                srcSet={`${lexiaPortrait768Jpg} 768w, ${lexiaPortrait1080Jpg} 1080w`}
                                sizes="(min-width: 1024px) 380px, 100vw"
                                alt="Lexia, votre coach linguistique — jeune femme souriante"
                                aria-label="Lexia, votre coach linguistique"
                                className="relative rounded-3xl w-full h-auto object-cover shadow-lg shadow-black/10 border border-lexiBorder"
                                loading="lazy"
                                decoding="async"
                            />
                        </picture>
                        <figcaption className="absolute -bottom-4 -left-4 bg-white border border-lexiBorder rounded-full px-4 py-1.5 shadow-md shadow-black/10 text-[11px] tracking-[0.22em] uppercase text-ink">
                            Lexia · Coach vocale
                        </figcaption>
                    </figure>
                </div>

                {/* Method */}
                <div
                    className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    data-testid="method-grid"
                >
                    <div className="col-span-full mb-2">
                        <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-2">
                            Méthode
                        </p>
                        <h2 className="font-serif text-2xl sm:text-3xl">
                            Neuf étapes, un seul objectif : ancrer.
                        </h2>
                    </div>
                    {STEPS.map((s, i) => (
                        <div
                            key={i}
                            data-testid={`method-step-${i}`}
                            className="bg-white border border-lexiBorder rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md hover:shadow-black/5 transition-transform duration-300"
                        >
                            <div className="flex items-center gap-2">
                                <s.icon className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                                <span className="text-[10px] tracking-[0.22em] uppercase text-inkSoft font-semibold">
                                    Étape {i + 1}
                                </span>
                            </div>
                            <h3 className="mt-3 font-serif text-xl">{s.label}</h3>
                            <p className="mt-1 text-sm text-inkSoft leading-relaxed">
                                {s.detail}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}
